import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface LobbyInfo {
  lobbyId: string;
  name: string;
  participantCount: number;
  passwordProtected: boolean;
}

interface LobbyState {
  connectedToLobby: boolean;
  lobbyId?: string;
  /** Human-readable name of the joined lobby; the id is a UUID and means nothing to players. */
  lobbyName?: string;
  team1: string[];
  team2: string[];
  participants: string[];
  lobbiesList: LobbyInfo[];
  host?: string;
  readyPlayers: string[];
  bots: Record<string, string>;
  sessionTarget?: number;
}

const initialState: LobbyState = {
  connectedToLobby: false,
  lobbyId: undefined,
  lobbyName: undefined,
  team1: [],
  team2: [],
  participants: [],
  lobbiesList: [],
  host: undefined,
  readyPlayers: [],
  bots: {},
  sessionTarget: undefined
};

export const lobbySlice = createSlice({
  name: "lobby",
  initialState,
  reducers: {
    connectToLobby: (state, action: PayloadAction<string>) => {
      state.connectedToLobby = true;
      state.lobbyId = action.payload;
    },
    // Leaving a lobby says nothing about which lobbies exist, and LobbyTable only
    // fetches the list on mount — resetting it wholesale left the page claiming
    // there were no lobbies at all until a reload.
    disconnectFromLobby: (state) => ({ ...initialState, lobbiesList: state.lobbiesList }),

    joinTeam: (
      state,
      action: PayloadAction<{ team: "TEAM1" | "TEAM2"; user: string }>
    ) => {
      const { team, user } = action.payload;

      if (team === "TEAM1") {
        state.team1.push(user);
        state.team2 = state.team2.filter(u => u !== user);
      } else {
        state.team2.push(user);
        state.team1 = state.team1.filter(u => u !== user);
      }
    },

    setTeam: (
      state,
      action: PayloadAction<{ team: "TEAM1" | "TEAM2"; users: string[] }>
    ) => {
      if (action.payload.team === "TEAM1") {
        state.team1 = action.payload.users;
      } else {
        state.team2 = action.payload.users;
      }
    },

    leaveTeam: (
      state,
      action: PayloadAction<{ team: "TEAM1" | "TEAM2" | "UNDEFINED"; user: string }>
    ) => {
      const { team, user } = action.payload;
      if (team ==="UNDEFINED"){
        state.team2 = state.team2.filter(u => u !== user);
        state.team1 = state.team1.filter(u => u !== user);
      }
      if (team === "TEAM1") {
        state.team2 = state.team2.filter(u => u !== user);
      } else if (team === "TEAM2") {
        state.team1 = state.team1.filter(u => u !== user);
      }
    },

    appendParticipant: (state, action: PayloadAction<{ user: string }>) => {
      state.participants.push(action.payload.user);
    },

    removeParticipant: (state, action: PayloadAction<{ user: string }>) => {
      const user = action.payload.user;
      state.participants = state.participants.filter(u => u !== user);
      state.team1 = state.team1.filter(u => u !== user);
      state.team2 = state.team2.filter(u => u !== user);
    },
    setLobbyName: (state, action: PayloadAction<string | undefined>) => {
      state.lobbyName = action.payload;
    },


    setParitipants: (state, action: PayloadAction<string[]>) => {
      state.participants = action.payload;
    },

    reset: () => initialState,

    updateLobbiesList: (state, action: PayloadAction<LobbyInfo>) => {
      const { lobbyId } = action.payload;
      const index = state.lobbiesList.findIndex(l => l.lobbyId === lobbyId);

      if (index !== -1) {
        state.lobbiesList[index] = action.payload;
      } else {
        state.lobbiesList.push(action.payload);
      }
    },

    setLobbiesList: (state, action: PayloadAction<{ lobbies: LobbyInfo[] }>) => {
      state.lobbiesList = action.payload.lobbies;
    },

    removeLobbyFromList: (state, action: PayloadAction<{ lobbyId: string }>) => {
      state.lobbiesList = state.lobbiesList.filter(
        l => l.lobbyId !== action.payload.lobbyId
      );
    },

    setHost: (state, action: PayloadAction<string>) => {
      state.host = action.payload;
    },

    setReadyPlayers: (state, action: PayloadAction<string[]>) => {
      state.readyPlayers = action.payload;
    },

    toggleReadyPlayer: (state, action: PayloadAction<{ user: string; ready: boolean }>) => {
      const { user, ready } = action.payload;
      state.readyPlayers = ready
        ? [...state.readyPlayers.filter(u => u !== user), user]
        : state.readyPlayers.filter(u => u !== user);
    },

    setBots: (state, action: PayloadAction<Record<string, string>>) => {
      state.bots = action.payload;
    },

    addBot: (
      state,
      action: PayloadAction<{ botName: string; team: "TEAM1" | "TEAM2"; difficulty: string }>
    ) => {
      const { botName, team, difficulty } = action.payload;
      state.bots[botName] = difficulty;
      if (team === "TEAM1") {
        state.team1.push(botName);
      } else {
        state.team2.push(botName);
      }
    },

    removeBot: (state, action: PayloadAction<{ botName: string }>) => {
      const { botName } = action.payload;
      delete state.bots[botName];
      state.team1 = state.team1.filter(u => u !== botName);
      state.team2 = state.team2.filter(u => u !== botName);
    },

    setSessionTarget: (state, action: PayloadAction<number | undefined>) => {
      state.sessionTarget = action.payload;
    }
  }
});

export const {
  setLobbyName,
  connectToLobby,
  disconnectFromLobby,
  joinTeam,
  leaveTeam,
  appendParticipant,
  removeParticipant,
  setParitipants,
  setTeam,
  reset,
  updateLobbiesList,
  setLobbiesList,
  removeLobbyFromList,
  setHost,
  setReadyPlayers,
  toggleReadyPlayer,
  setBots,
  addBot,
  removeBot,
  setSessionTarget
} = lobbySlice.actions;

export default lobbySlice.reducer;
